import { classifyApiError, extractErrorMessage, type NormalizedApiError } from '@/utils/apiError'

export interface EnvelopeReviewSaveFailure extends NormalizedApiError {
  retryable: boolean
}

const REVIEW_CONFLICT_MESSAGE =
  'The review changed since you opened it, so this action could not be completed. ' +
  'Copy your unsaved corrections before discarding them and opening the latest review.'

export interface EnvelopeReviewLifecycleSession<TSnapshot> {
  baseline: TSnapshot | null
  working: TSnapshot | null
  effectiveContentHash: string
  originalContentHash: string
  reviewIdentityHash: string
  loading: boolean
  saving: boolean
  resetting: boolean
  completing: boolean
  conflict: boolean
  writable: boolean
  error: string | null
  completionError?: string | null
  completionFailure?: EnvelopeReviewSaveFailure | null
  saveFailure?: EnvelopeReviewSaveFailure | null
  requestGeneration: number
}

export interface EnvelopeReviewLoadFailure {
  message: string
  status: number | undefined
  retryable: boolean
}

interface EnvelopeReviewSessionWithLoadFailure<
  TSnapshot,
> extends EnvelopeReviewLifecycleSession<TSnapshot> {
  loadFailure: EnvelopeReviewLoadFailure | null
}

interface ExpectedReviewHashes {
  expected_effective_hash: string
  expected_review_identity_hash: string
}

interface ReviewSavePayload<TSnapshot> extends ExpectedReviewHashes {
  snapshot: TSnapshot
}

interface ReviewCompletionPayload extends ExpectedReviewHashes {
  marked_done: boolean
}

interface EnvelopeReviewLifecycleConfig<
  TSnapshot,
  TReview,
  TSession extends EnvelopeReviewLifecycleSession<TSnapshot>,
> {
  applyReview: (session: TSession, review: TReview) => void
  cloneSnapshot: (snapshot: TSnapshot) => TSnapshot
  conflictCode: string
  ensureSession: (projectId: number, fileId: string) => TSession
  getReview: (projectId: number, fileId: string) => Promise<TReview>
  saveReview: (
    projectId: number,
    fileId: string,
    requestBody: ReviewSavePayload<TSnapshot>,
  ) => Promise<TReview>
  resetReview: (
    projectId: number,
    fileId: string,
    requestBody: ExpectedReviewHashes,
  ) => Promise<TReview>
  setReviewCompletion: (
    projectId: number,
    fileId: string,
    requestBody: ReviewCompletionPayload,
  ) => Promise<TReview>
  snapshotsEqual: (left: TSnapshot | null, right: TSnapshot | null) => boolean
  onLoadStart?: (session: TSession) => void
  onLoadError?: (session: TSession, error: unknown) => void
  onMutationError?: (session: TSession, error: unknown) => void
}

export function createEnvelopeReviewLifecycle<
  TSnapshot,
  TReview,
  TSession extends EnvelopeReviewLifecycleSession<TSnapshot> =
    EnvelopeReviewLifecycleSession<TSnapshot>,
>(config: EnvelopeReviewLifecycleConfig<TSnapshot, TReview, TSession>) {
  async function load(
    projectId: number,
    fileId: string,
    options: { force?: boolean } = {},
  ): Promise<boolean> {
    const session = config.ensureSession(projectId, fileId)
    if (
      !options.force &&
      session.working &&
      (session.conflict || !config.snapshotsEqual(session.baseline, session.working))
    ) {
      return true
    }
    const generation = ++session.requestGeneration
    session.saving = false
    session.resetting = false
    session.completing = false
    session.loading = true
    session.error = null
    if ('completionError' in session) session.completionError = null
    if ('completionFailure' in session) session.completionFailure = null
    session.saveFailure = null
    config.onLoadStart?.(session)
    try {
      const review = await config.getReview(projectId, fileId)
      if (session.requestGeneration !== generation) return false
      config.applyReview(session, review)
      return true
    } catch (error) {
      if (session.requestGeneration === generation) {
        session.error = extractErrorMessage(error)
        config.onLoadError?.(session, error)
      }
      return false
    } finally {
      if (session.requestGeneration === generation) session.loading = false
    }
  }

  function replaceWorking(projectId: number, fileId: string, snapshot: TSnapshot): void {
    const session = config.ensureSession(projectId, fileId)
    if (session.conflict || !session.writable) return
    session.working = config.cloneSnapshot(snapshot)
    session.error = null
    if ('completionError' in session) session.completionError = null
    if ('completionFailure' in session) session.completionFailure = null
    session.saveFailure = null
  }

  async function save(projectId: number, fileId: string): Promise<boolean> {
    const session = config.ensureSession(projectId, fileId)
    if (!mutationReady(session)) return false
    if (config.snapshotsEqual(session.baseline, session.working)) return true
    const submitted = config.cloneSnapshot(session.working!)
    const generation = ++session.requestGeneration
    session.saving = true
    session.error = null
    session.saveFailure = null
    try {
      const review = await config.saveReview(projectId, fileId, {
        expected_effective_hash: session.effectiveContentHash,
        expected_review_identity_hash: session.reviewIdentityHash,
        snapshot: submitted,
      })
      if (session.requestGeneration !== generation) return false
      applySavedReview(session, review, submitted)
      return true
    } catch (error) {
      if (session.requestGeneration !== generation) return false
      const classified = classifyApiError(error)
      if (retryableSaveStatus(classified.status) || classified.code === config.conflictCode) {
        return await reconcileFailedSave(projectId, fileId, session, submitted, error, generation)
      }
      recordMutationError(session, error)
      session.saveFailure = { ...classified, retryable: false }
      return false
    } finally {
      if (session.requestGeneration === generation) session.saving = false
    }
  }

  async function reset(projectId: number, fileId: string): Promise<boolean> {
    const session = config.ensureSession(projectId, fileId)
    if (!mutationReady(session)) return false
    const generation = ++session.requestGeneration
    session.resetting = true
    session.error = null
    try {
      const review = await config.resetReview(projectId, fileId, expectedHashes(session))
      if (session.requestGeneration !== generation) return false
      config.applyReview(session, review)
      return true
    } catch (error) {
      if (session.requestGeneration !== generation) return false
      recordMutationError(session, error)
      return false
    } finally {
      if (session.requestGeneration === generation) session.resetting = false
    }
  }

  async function setCompletion(
    projectId: number,
    fileId: string,
    markedDone: boolean,
  ): Promise<boolean> {
    const session = config.ensureSession(projectId, fileId)
    if (!mutationReady(session)) return false
    if (
      !config.snapshotsEqual(session.baseline, session.working) &&
      !(await save(projectId, fileId))
    ) {
      return false
    }
    if (!mutationReady(session) || !config.snapshotsEqual(session.baseline, session.working)) {
      return false
    }
    const generation = ++session.requestGeneration
    session.completing = true
    session.error = null
    if ('completionError' in session) session.completionError = null
    if ('completionFailure' in session) session.completionFailure = null
    try {
      const review = await config.setReviewCompletion(projectId, fileId, {
        ...expectedHashes(session),
        marked_done: markedDone,
      })
      if (session.requestGeneration !== generation) return false
      config.applyReview(session, review)
      return true
    } catch (error) {
      if (session.requestGeneration !== generation) return false
      recordMutationError(session, error)
      if ('completionError' in session) {
        const classified = classifyApiError(error)
        session.completionError =
          classified.kind === 'dev'
            ? 'We couldn’t confirm the completion status. Retry this action; if it keeps failing, contact support.'
            : session.error
        session.completionFailure = {
          ...classified,
          retryable: retryableSaveStatus(classified.status),
        }
        session.error = null
      }
      return false
    } finally {
      if (session.requestGeneration === generation) session.completing = false
    }
  }

  async function reloadAfterConflict(
    projectId: number,
    fileId: string,
    options: { discardLocal: boolean },
  ): Promise<boolean> {
    if (!options.discardLocal) return false
    return load(projectId, fileId, { force: true })
  }

  function applySavedReview(session: TSession, review: TReview, submitted: TSnapshot): void {
    const newerWorking = config.snapshotsEqual(session.working, submitted)
      ? null
      : config.cloneSnapshot(session.working!)
    config.applyReview(session, review)
    session.saveFailure = null
    if (newerWorking) session.working = newerWorking
  }

  async function reconcileFailedSave(
    projectId: number,
    fileId: string,
    session: TSession,
    submitted: TSnapshot,
    originalError: unknown,
    generation: number,
  ): Promise<boolean> {
    const classified = classifyApiError(originalError)
    let conflict = classified.code === config.conflictCode
    try {
      const review = await config.getReview(projectId, fileId)
      if (session.requestGeneration !== generation) return false
      const server = review as {
        snapshot: TSnapshot
        original_content_hash: string
        effective_content_hash: string
        review_identity_hash: string
      }
      const originalChanged = server.original_content_hash !== session.originalContentHash
      const newerEditsOnChangedIdentity =
        !config.snapshotsEqual(session.working, submitted) &&
        server.review_identity_hash !== session.reviewIdentityHash
      if (
        !originalChanged &&
        !newerEditsOnChangedIdentity &&
        config.snapshotsEqual(server.snapshot, submitted)
      ) {
        applySavedReview(session, review, submitted)
        return true
      }
      conflict ||= Boolean(
        originalChanged ||
        server.effective_content_hash !== session.effectiveContentHash ||
        server.review_identity_hash !== session.reviewIdentityHash ||
        !config.snapshotsEqual(server.snapshot, session.baseline),
      )
    } catch {
      // A failed read cannot prove a conflict or authorize discarding the working corrections.
    }
    if (session.requestGeneration !== generation) return false
    const message = conflict
      ? REVIEW_CONFLICT_MESSAGE
      : 'We couldn’t confirm that your corrections were saved. Select Retry save to try again. ' +
        'Your unsaved corrections are only in this tab. Refreshing or closing it will lose them.'
    session.conflict = conflict
    session.error = message
    session.saveFailure = {
      ...classified,
      code: conflict ? config.conflictCode : 'review_save_unconfirmed',
      message,
      retryable: !conflict,
    }
    return false
  }

  function recordMutationError(
    session: EnvelopeReviewLifecycleSession<TSnapshot>,
    error: unknown,
  ): void {
    const classified = classifyApiError(error)
    session.error = extractErrorMessage(error)
    session.saveFailure = null
    if (classified.code === config.conflictCode) {
      session.conflict = true
      session.error = REVIEW_CONFLICT_MESSAGE
    }
    config.onMutationError?.(session as TSession, error)
  }

  return {
    load,
    replaceWorking,
    save,
    reset,
    setCompletion,
    reloadAfterConflict,
  }
}

function retryableSaveStatus(status: number | undefined): boolean {
  return status === undefined || status === 408 || status === 429 || status >= 500
}

export function envelopeReviewLoadHooks<
  TSnapshot,
  TSession extends EnvelopeReviewSessionWithLoadFailure<TSnapshot>,
>() {
  return {
    onLoadStart(session: TSession): void {
      session.loadFailure = null
    },
    onLoadError(session: TSession, error: unknown): void {
      const { status } = classifyApiError(error)
      session.loadFailure = {
        message: extractErrorMessage(error),
        status,
        retryable: status === undefined || status === 408 || status === 429 || status >= 500,
      }
    },
  }
}

export function clearEnvelopeReviewSessions<TSession extends { requestGeneration: number }>(
  sessions: Map<string, TSession>,
): void {
  for (const session of sessions.values()) session.requestGeneration += 1
  sessions.clear()
}

export function releaseEnvelopeReviewSession<
  TSnapshot,
  TSession extends EnvelopeReviewLifecycleSession<TSnapshot>,
>(
  sessions: Map<string, TSession>,
  key: string,
  snapshotsEqual: (left: TSnapshot | null, right: TSnapshot | null) => boolean,
): void {
  const session = sessions.get(key)
  if (!session || session.conflict || !snapshotsEqual(session.baseline, session.working)) return
  session.requestGeneration += 1
  sessions.delete(key)
}

function mutationReady<TSnapshot>(session: EnvelopeReviewLifecycleSession<TSnapshot>): boolean {
  return Boolean(
    !session.conflict &&
    session.writable &&
    session.baseline &&
    session.working &&
    session.effectiveContentHash &&
    session.reviewIdentityHash,
  )
}

function expectedHashes<TSnapshot>(
  session: EnvelopeReviewLifecycleSession<TSnapshot>,
): ExpectedReviewHashes {
  return {
    expected_effective_hash: session.effectiveContentHash,
    expected_review_identity_hash: session.reviewIdentityHash,
  }
}
